import * as THREE from 'three';
import {
  DEG_TO_RAD,
  OFFSCREEN_DOT_COLOR_HEX,
  OFFSCREEN_DOT_DISTANCE_M,
  OFFSCREEN_DOT_RADIUS_M,
  OFFSCREEN_EDGE_ANGLE_DEG,
  OFFSCREEN_HALO_COLOR_HEX,
  OFFSCREEN_HALO_OPACITY,
  OFFSCREEN_HALO_RADIUS_M,
  OFFSCREEN_RING_COLOR_HEX,
  OFFSCREEN_RING_INNER_RADIUS_M,
  OFFSCREEN_RING_OUTER_RADIUS_M,
  OFFSCREEN_SHOW_MARGIN,
} from '../config/constants';

/** Scratch vectors; transient, never retained. */
const _local = new THREE.Vector3();
const _world = new THREE.Vector3();

/**
 * A flat, always-on-top layer of the indicator glyph (halo / ring / dot). Each layer is
 * its own mesh because the three colors need their own material; `depthTest` off makes it
 * a navigational hint that the world never occludes, so draw order (renderOrder) and a
 * tiny forward z-offset stack the layers correctly.
 */
function makeLayer(
  geom: THREE.BufferGeometry,
  color: number,
  renderOrder: number,
  opacity = 1,
): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color,
    side: THREE.DoubleSide,
    transparent: true,
    opacity,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = renderOrder;
  mesh.frustumCulled = false;
  return mesh;
}

/**
 * Off-screen target indicator: a miniature copy of the target — a terracotta ring + cream
 * center dot on a cream halo — pinned to the edge of the field of view, pointing toward a
 * target that sits outside it. Hidden whenever the target is inside the view. Reusing the
 * target's own colors makes the hint read as "your target is over there"; the cream halo
 * keeps it findable in low-acuity peripheral vision against forest/wood backdrops.
 *
 * Lives in world space (added to the scene), re-placed in front of the head every frame
 * and turned to face the user. Positioning in world space — rather than as a camera child
 * — keeps it independent of camera-child render quirks and of the per-eye offset of the XR
 * union frustum.
 *
 * The FOV half-angles are read off the camera's `projectionMatrix`, which three.js copies
 * from the union (both-eyes) XR projection each render, so they reflect the real headset
 * FOV (one frame stale during tick — irrelevant for an indicator).
 */
export class OffscreenIndicator {
  readonly root: THREE.Group;
  private parent: THREE.Object3D | null = null;

  constructor() {
    const halo = makeLayer(
      new THREE.CircleGeometry(OFFSCREEN_HALO_RADIUS_M, 24),
      OFFSCREEN_HALO_COLOR_HEX,
      999,
      OFFSCREEN_HALO_OPACITY,
    );
    halo.name = 'offscreen_halo';

    const ring = makeLayer(
      new THREE.RingGeometry(OFFSCREEN_RING_INNER_RADIUS_M, OFFSCREEN_RING_OUTER_RADIUS_M, 32),
      OFFSCREEN_RING_COLOR_HEX,
      1000,
    );
    ring.name = 'offscreen_ring';
    // Stack above the halo — depthTest is off, so a tiny z-offset settles draw order.
    ring.position.z = 0.0005;

    const dot = makeLayer(
      new THREE.CircleGeometry(OFFSCREEN_DOT_RADIUS_M, 16),
      OFFSCREEN_DOT_COLOR_HEX,
      1001,
    );
    dot.name = 'offscreen_dot';
    dot.position.z = 0.001;

    this.root = new THREE.Group();
    this.root.name = 'offscreen_indicator';
    this.root.frustumCulled = false;
    this.root.visible = false;
    this.root.add(halo, ring, dot);
  }

  /** Add the glyph to the scene, moving it off any previous parent. Idempotent. */
  attachTo(scene: THREE.Object3D): void {
    if (this.parent === scene) return;
    this.parent?.remove(this.root);
    scene.add(this.root);
    this.parent = scene;
  }

  /**
   * Show the glyph at the FOV edge toward `worldPos` when that point is outside the
   * view; hide it when in view. Pass the same camera the scene renders with.
   */
  update(camera: THREE.Camera, worldPos: THREE.Vector3): void {
    _local.copy(worldPos);
    camera.worldToLocal(_local); // camera frame: -Z forward, +X right, +Y up

    const m = (camera as THREE.PerspectiveCamera).projectionMatrix.elements;
    const tanHHalf = 1 / m[0]; // tan of the horizontal half-FOV
    const tanVHalf = 1 / m[5]; // tan of the vertical half-FOV

    const inFront = _local.z < 0;
    const depth = -_local.z;
    const inView =
      inFront &&
      Math.abs(_local.x / depth) <= tanHHalf * OFFSCREEN_SHOW_MARGIN &&
      Math.abs(_local.y / depth) <= tanVHalf * OFFSCREEN_SHOW_MARGIN;

    if (inView) {
      this.root.visible = false;
      return;
    }

    // Direction toward the target in the camera's view plane (right, up). Uses local
    // x/y directly — not divided by depth — so a target *behind* the head still points
    // the way you'd turn (a depth divide would flip the sign there).
    const dirLen = Math.hypot(_local.x, _local.y) || 1;
    const dirX = _local.x / dirLen;
    const dirY = _local.y / dirLen;

    // Place the glyph on a fixed cone around the view axis (-Z), toward the target, then
    // lift into world space. Fixed angle ⇒ always inside each eye's rendered FOV.
    const phi = OFFSCREEN_EDGE_ANGLE_DEG * DEG_TO_RAD;
    const r = Math.sin(phi) * OFFSCREEN_DOT_DISTANCE_M;
    _world.set(dirX * r, dirY * r, -Math.cos(phi) * OFFSCREEN_DOT_DISTANCE_M);
    _world.applyMatrix4(camera.matrixWorld);

    this.root.position.copy(_world);
    camera.getWorldQuaternion(this.root.quaternion); // face the user
    this.root.visible = true;
  }

  hide(): void {
    this.root.visible = false;
  }

  dispose(): void {
    this.parent?.remove(this.root);
    this.parent = null;
    this.root.traverse((o) => {
      if ((o as THREE.Mesh).geometry) (o as THREE.Mesh).geometry.dispose();
      const mat = (o as THREE.Mesh).material;
      if (mat) {
        if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
        else mat.dispose();
      }
    });
  }
}
